import {
  AuthMessage,
  AuthMessageType,
  ClientType,
  HostMessageType,
  PeerIdType,
  PresenceMessageType,
  websocket,
} from '@playcastdotio/Messaging';
import { notifyHostAuthSuccess, requestPeerId, sendMessage, setIsReady, disconnect, handleReconnect } from '..';
import { getStateStore } from '../../../stores';
import { queryOwnerAliases, queryProfile } from './profile';
import { sendMatchmakerHostConfig } from '../../managers/matchmaker';
import * as Sentry from '@sentry/react';
import constants from '@playcastdotio/GlobalConfig';
import { USE_SESSION_COORDINATOR, USE_MESSAGE_ROUTER } from '../../../../config/featureFlags';
import { appCoordinator } from '../../../../coordinators/init';

export const handleAuthMessage = (message: AuthMessage) => {
  console.log('Auth message:', message);
  switch (message.type) {
    case AuthMessageType.AUTH_ERROR: {
      const errorData = (message as AuthMessage & { error?: { code?: string; message?: string } }).error;
      const code = errorData?.code ?? 'UNKNOWN';
      const errorMessage = errorData?.message ?? 'Unknown auth error';

      console.error(`[Auth] AUTH_ERROR received: code=${code}, message=${errorMessage}`);
      Sentry.captureMessage(`[Auth] Server rejected authentication: ${code}`, {
        level: 'error',
        tags: { auth_error_code: code },
        extra: {
          errorMessage,
          guest_mode_enabled: getStateStore('ipc').hostMatchmakingState.currentState.guestAccount,
          edition: getStateStore('ipc').hostState.currentState.edition,
        },
      });

      disconnect();
      handleReconnect();
      break;
    }
    case AuthMessageType.AUTH_SUCCESS: {
      console.log('Auth success');
      setIsReady(true);
      const state = getStateStore('ipc');

      // Apply server-confirmed cloud host status
      const isCloudHost = !!message.isCloudHost;
      if (isCloudHost) {
        console.log('[Auth] ✅ Server confirmed this host as a cloud host');
      }
      appCoordinator.updateCloudHostStatus(isCloudHost);

      notifyHostAuthSuccess();

      // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
      if (!USE_SESSION_COORDINATOR) {
        state.hostPresenceState.updateState({
          ...state.hostPresenceState.currentState,
          isCloudHost,
        });

        sendMessage(
          websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
            clientType: ClientType.HOST,
            presence: {
              ...state.hostPresenceState.currentState,
              isCloudHost,
            },
          }),
        );
      }

      sendMessage(
        websocket.generators.host(HostMessageType.HOST_SETTINGS, {
          displayName: state.machineNameState.currentState,
          maxPlayerCount: state.hostSettingsState.currentState.maxPlayerCount,
          version: state.versionState.currentState.version,
          frontendVersion: constants.VERSION,
        }),
      );
      // const pingMap = state.networkMetricsState.currentState.ping;
      // if (Object.keys(pingMap).length > 0) {
      //   sendMessage(
      //     websocket.generators.vpn(VpnMessageType.VPN_CONNECT_REQUEST, {
      //       pingMap,
      //     }),
      //   );
      // } else {
      //   const waitForPingMap = async () => {
      //     // make a promise waiting for the ping map to be populated
      //     return await new Promise<LatencyResults>((resolve) => {
      //       const interval = setInterval(() => {
      //         const pingMap = state.networkMetricsState.currentState.ping;
      //         if (Object.keys(pingMap).length > 0) {
      //           clearInterval(interval);
      //           resolve(pingMap);
      //         }
      //       }, 1000);
      //     });
      //   };
      //   waitForPingMap().then((awaitedPingMap) => {
      //     if (awaitedPingMap) {
      //       sendMessage(
      //         websocket.generators.vpn(VpnMessageType.VPN_CONNECT_REQUEST, {
      //           pingMap: awaitedPingMap,
      //         }),
      //       );
      //     }
      //   });
      // }
      // console.log('Sending invitation store message from handleAuthMessage');
      // sendMessage(
      //   websocket.generators.invitation(InvitationMessageType.INVITATION_STORE, {
      //     invitation: {
      //       ...state.invitationStateV2.currentState,
      //       invalid:
      //         state.hostState.currentState.status === 'streaming' || state.hostState.currentState.status === 'starting' ? false : true,
      //     },
      //   }),
      // );
      // If we are already streaming, we need to request a new peer ID
      if (state.hostState.currentState.status === 'streaming') {
        requestPeerId(PeerIdType.HOST);
      }

      if (state.hostState.currentState.edition !== 'standard') {
        // Request profile of the user.
        queryProfile();
        queryOwnerAliases();

        // STRANGLER-FIG: Skip legacy matchmaker config when new coordinator architecture is active
        // PavilionCoordinator will handle HOST_CONFIG messages on its own
        if (!USE_MESSAGE_ROUTER) {
          // CRITICAL: Re-send matchmaking config after reconnection to preserve state
          // This ensures the host remains in the matchmaking pool after websocket reconnects
          console.log(`[Auth] Reconnected - re-sending matchmaking config for edition: ${state.hostState.currentState.edition}`);
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Reconnected - re-sending matchmaking config',
            level: 'info',
            data: {
              edition: state.hostState.currentState.edition,
              matchmakeable: state.hostMatchmakingState.currentState.matchmakeable,
              // Guest mode context
              guest_mode_enabled: state.hostMatchmakingState.currentState.guestAccount,
              ad_mode_enabled: state.adModeState.currentState.enabled,
              game_launched: state.playjectorHostModeState.currentState.gameLaunched,
              user_logged_in: state.playjectorHostModeState.currentState.userLoggedIn,
              steam_launched: state.playjectorHostModeState.currentState.steamLaunched,
              host_status: state.hostState.currentState.status,
            },
          });
          sendMatchmakerHostConfig().catch((err) => {
            console.error('[Auth] Failed to send host config after auth:', err);
          });
        } else {
          console.log('[Auth] Strangler-fig enabled - PavilionCoordinator will handle HOST_CONFIG on reconnection');
        }
      }
    }
  }
};
